import { useEffect, useState } from "react";
import api from "@/lib/api";

// Human-readable labels for backend enum values used across the platform.
export const LABELS = {
  question_types: {
    mcq_single: "Multiple Choice (Single)",
    mcq_multi: "Multiple Choice (Multiple)",
    true_false: "True / False",
    yes_no: "Yes / No",
    fill_blank: "Fill in the Blank",
    short_answer: "Short Answer",
    long_answer: "Long Answer",
    essay: "Essay",
    numeric: "Numeric Answer",
    date_answer: "Date Answer",
    clinical_scenario: "Clinical Scenario",
    coding_scenario: "Coding Scenario",
    case_study: "Case Study",
    mcq: "Multiple Choice (legacy)",
  },
  assessment_levels: {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    expert: "Expert",
  },
  assessment_types: {
    topic_wise: "Topic-wise Test",
    module_test: "Module Test",
    weekly: "Weekly Assessment",
    mock_test: "Mock Test",
    grand_test: "Grand Test",
    final_certification: "Final Certification Exam",
  },
  result_statuses: {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    failed: "Failed",
    passed: "Passed",
    certified: "Certified",
  },
  difficulties: {
    easy: "Easy",
    medium: "Medium",
    hard: "Hard",
    expert: "Expert",
  },
};

export const FALLBACK_CONSTANTS = {
  question_types: Object.keys(LABELS.question_types).filter((t) => t !== "mcq"),
  question_categories: [
    "ICD-10-CM", "OASIS", "PDGM", "Compliance", "Quality",
    "Clinical Documentation", "Sequencing", "Medical Terminology",
    "Anatomy", "Physiology", "Pharmacology",
  ],
  chapters: [
    "Certain Infectious and Parasitic Diseases",
    "Neoplasms",
    "Diseases of the Blood and Blood-forming Organs and Certain Disorders Involving the Immune Mechanism",
    "Endocrine, Nutritional and Metabolic Diseases",
    "Mental, Behavioral and Neurodevelopmental Disorders",
    "Diseases of the Nervous System",
    "Diseases of the Eye and Adnexa",
    "Diseases of the Ear and Mastoid Process",
    "Diseases of the Circulatory System",
    "Diseases of the Respiratory System",
    "Diseases of the Digestive System",
    "Diseases of the Skin and Subcutaneous Tissue",
    "Diseases of the Musculoskeletal System and Connective Tissue",
    "Diseases of the Genitourinary System",
    "Pregnancy, Childbirth and the Puerperium",
    "Certain Conditions Originating in the Perinatal Period",
    "Congenital Malformations, Deformations and Chromosomal Abnormalities",
    "Symptoms, Signs and Abnormal Clinical and Laboratory Findings, Not Elsewhere Classified",
    "Injury, Poisoning and Certain Other Consequences of External Causes",
    "External Causes of Morbidity",
    "Factors Influencing Health Status and Contact with Health Services",
    "Codes for Special Purposes",
  ],
  assessment_levels: Object.keys(LABELS.assessment_levels),
  assessment_types: Object.keys(LABELS.assessment_types),
  certifications: [
    "Home Health Coding Foundation",
    "Home Health Coding Professional",
    "Home Health Coding Advanced",
    "OASIS Specialist",
    "Home Health Medical Coding Certified",
  ],
  trainer_designations: ["Trainer", "Senior Trainer", "Team Lead", "Manager", "Quality Analyst", "Administrator"],
  result_statuses: Object.keys(LABELS.result_statuses),
  difficulties: Object.keys(LABELS.difficulties),
};

// Module-scoped cache so multiple components share a single fetch.
let _cache = null;
let _inflight = null;

export function useConstants() {
  const [data, setData] = useState(_cache || FALLBACK_CONSTANTS);

  useEffect(() => {
    if (_cache) return;
    if (!_inflight) {
      _inflight = api.get("/constants").then(
        (res) => { _cache = res.data; return res.data; },
        () => FALLBACK_CONSTANTS,
      );
    }
    _inflight.then((d) => setData(d));
  }, []);

  return data;
}

export function labelFor(group, value) {
  if (!value) return "";
  return (LABELS[group] && LABELS[group][value]) || value;
}
